import type { ReactElement } from "react";
import { Link } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { loadFailureMessage } from "../../components/load-failure.js";
import { FormSummary, TextField } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { invoiceUrls } from "../invoices/urls.js";

import type { CenterInvoiceList, CenterInvoiceRow, CenterInvoiceSettings } from "./tenant-types.js";
import { adminCenterApi } from "./urls.js";

/**
 * 最後の確認はブラウザの確認ダイアログで行う（重要な操作は最後にダイアログで確かめる。2026-10-10 に利用者が決めた）。
 * 仕切り率は請求額を、見られる人は請求書を誰に見せるかを変える。
 */
export const invoiceSettingsDialog = (title: string, discountRatePercent: string, invoiceReaders: string): string =>
    `「${title}」の請求の設定を保存します。仕切り率は ${discountRatePercent}%、`
    + `請求書を見られる人は「${invoiceReaders.trim() === "" ? "契約者だけ" : invoiceReaders}」になります。`
    + "固定済みの請求書は変わりません。よろしいですか？";

/** 請求書を作り直す前に聞く文。 */
export const regenerateInvoiceDialog = (title: string, monthLabel: string): string =>
    `「${title}」の${monthLabel}の請求書を作り直します。いまの内容で新しい版を作ります（前の版は残ります）。よろしいですか？`;

/**
 * 請求の設定（仕切り率・宛名・見られる人）。<b>請求の権限を持つ運営だけに届く</b>（サーバが決める）。
 * 請求先は持たない——請求書の宛先は宛名（空なら企業名）だけ。
 * 保存しても固定済みの請求書は変わらない——変えるときは下の一覧で作り直す。API の断りは欄の下に出す。
 */
export function InvoiceSettingsSection({ tenantId, title, settings }: {
    readonly tenantId: number;
    /** 契約のシステム名（確認ダイアログの文に入れる）。 */
    readonly title: string;
    readonly settings: CenterInvoiceSettings;
}): ReactElement {
    const form = useApiForm(adminCenterApi.invoiceSettings(tenantId), undefined, (valueOf) => invoiceSettingsDialog(
        title, valueOf("discountRatePercent"), valueOf("invoiceReaders"),
    ));

    return (
        <section className="card mb-4 p-6">
            <h2 className="section-title">請求の設定</h2>
            <form method="post" onSubmit={form.onSubmit}>
                <FormSummary messages={form.summary} />
                <div className="grid gap-x-4 sm:grid-cols-2">
                    <TextField
                        label="仕切り率（%）"
                        name="discountRatePercent"
                        inputMode="numeric"
                        defaultValue={String(settings.discountRatePercent)}
                        help="0 は定価。代理店経由の契約で、単価に掛けて 10 円単位で切り上げます。"
                        error={form.fieldError("discountRatePercent")}
                    />
                    <TextField
                        label="請求書の宛名"
                        name="invoiceAddressee"
                        maxLength={128}
                        defaultValue={settings.invoiceAddressee}
                        help="空なら企業名を使います。"
                        error={form.fieldError("invoiceAddressee")}
                    />
                </div>
                <TextField
                    label="請求書を見られる人"
                    name="invoiceReaders"
                    maxLength={1024}
                    defaultValue={settings.invoiceReaders}
                    help="メールアドレスを ; で区切ります（例: keiri@example.com; partner@example.com）。この契約の利用者でなくてもかまいません（ポータルのアカウントでログインし、契約一覧の「請求書」から開きます）。空なら契約者だけ。指定するとそこに無い契約者は見られません（販売パートナーの担当者を指定し、仕切り価格を契約者に見せないときに使います）。"
                    error={form.fieldError("invoiceReaders")}
                />
                <button type="submit" className="btn-primary btn-sm" disabled={form.busy}>保存する</button>
            </form>
        </section>
    );
}

/** 固定した請求書を作り直すボタン。<b>前の版は残る</b>（サーバが版を足す）。 */
function RegenerateButton({ tenantId, title, row }: {
    readonly tenantId: number;
    readonly title: string;
    readonly row: CenterInvoiceRow;
}): ReactElement {
    const form = useApiForm(
        adminCenterApi.regenerateInvoice(tenantId, row.id), undefined, regenerateInvoiceDialog(title, row.monthLabel),
    );

    return (
        <form method="post" onSubmit={form.onSubmit}>
            <FormSummary messages={form.summary} />
            <button type="submit" className="btn-secondary btn-sm" disabled={form.busy}>
                この月の請求書を作り直す
            </button>
        </form>
    );
}

function InvoiceTable({ tenantId, title, rows }: {
    readonly tenantId: number;
    readonly title: string;
    readonly rows: readonly CenterInvoiceRow[];
}): ReactElement {
    if (rows.length === 0) {
        return <p className="text-sm text-slate-500">請求書はありません。</p>;
    }
    return (
        <div className="table-scroll">
            <table className="data-table">
                <thead>
                    <tr><th>月</th><th>ご請求金額</th><th>状態</th><th /></tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.id}>
                            <td>{row.monthLabel}</td>
                            <td>{row.totalLabel}</td>
                            <td>{row.stateLabel}</td>
                            <td className="cell-actions">
                                {/* 画面で請求書を開く（会員の請求書の画面と同じ。請求の権限があればどの契約も開ける）。印刷もそこから */}
                                <div className="flex flex-wrap justify-end gap-2">
                                    <Link to={invoiceUrls.showFromCenter(tenantId, row.id)} className="btn-secondary btn-sm">表示</Link>
                                    {row.canRegenerate ? <RegenerateButton tenantId={tenantId} title={title} row={row} /> : null}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * 請求書の一覧。<b>期間が終わった月は固定</b>されていて、作り直せる。月の途中は見込み。
 * 開いた時点で、期間が終わった月は固定される（会員が開いたときと同じ）。
 * <b>請求書を発行しない契約（無償利用・月額以外）は、月を並べず（金額も状態も出さない）、サーバが返す一文だけを出す</b>
 * （Implem.Portal.Backend の docs/features/13 §2-2）。
 */
export function InvoicesSection({ tenantId, title }: { readonly tenantId: number; readonly title: string }): ReactElement {
    const data = useApiData<CenterInvoiceList>(adminCenterApi.invoices(tenantId));
    // 画面全体の断り（ApiState）は出さない。契約の画面のほかの欄は読めている
    const waiting = data.state === "failed" ? loadFailureMessage("請求書", data) : "読み込んでいます…";
    // 請求書を発行しない契約は、表を描かずにサーバの一文だけ（金額や状態を出さない）
    const notice = data.state === "ready" ? data.value.exemptNotice : waiting;

    return (
        <section className="card mb-4 p-6">
            <h2 className="section-title">請求書</h2>
            {data.state === "ready" && notice === null
                ? <InvoiceTable tenantId={tenantId} title={title} rows={data.value.invoices} />
                : <p className="text-sm text-slate-500">{notice}</p>}
        </section>
    );
}
